import { Button, Card, Container, Eyebrow } from '@tt/design-system';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CeremonialLoader } from '../../components/CeremonialLoader';
import { ImageCropper } from '../../components/ImageCropper';
import { Stepper } from '../../components/Stepper';
import { TextArea } from '../../components/form/TextArea';
import { TextField } from '../../components/form/TextField';
import { VALIDATION_MESSAGES } from '../../data/loaderMessages';
import { MESSAGE_SUGGESTIONS } from '../../data/messageSuggestions';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { useDraftStore } from '../../store/draftStore';
import { fileToDataUrl } from '../../utils/fileToDataUrl';

const STEP_LABELS = ['Nome', 'Frase', 'Foto'];
const MAX_NAME = 30;
const MAX_MSG = 80;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

export default function Register() {
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const draft = useDraftStore((s) => s.draft);
  const setField = useDraftStore((s) => s.setField);
  const setStep = useDraftStore((s) => s.setStep);
  const next = useDraftStore((s) => s.next);
  const prev = useDraftStore((s) => s.prev);
  const runModeration = useDraftStore((s) => s.runModeration);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);

  const transition = reduced
    ? { duration: 0 }
    : { duration: 0.4, ease: [0.16, 1, 0.3, 1] as const };

  function validateStep1(): boolean {
    const name = draft.displayName.trim();
    if (name.length < 2) {
      setNameError('Use no mínimo 2 caracteres.');
      return false;
    }
    if (name.length > MAX_NAME) {
      setNameError(`Use no máximo ${MAX_NAME} caracteres.`);
      return false;
    }
    setNameError(null);
    return true;
  }

  function validateStep2(): boolean {
    const msg = draft.message.trim();
    if (msg.length < 3) {
      setMessageError('Escreva uma frase um pouco mais longa.');
      return false;
    }
    if (msg.length > MAX_MSG) {
      setMessageError(`Use no máximo ${MAX_MSG} caracteres.`);
      return false;
    }
    setMessageError(null);
    return true;
  }

  function validateStep3(): boolean {
    if (!draft.photoDataUrl) {
      setPhotoError('Envie uma foto pra continuar.');
      return false;
    }
    setPhotoError(null);
    return true;
  }

  async function handleNext() {
    if (draft.step === 1) {
      if (validateStep1()) next();
      return;
    }
    if (draft.step === 2) {
      if (validateStep2()) next();
      return;
    }
    if (draft.step === 3) {
      if (!validateStep3()) return;
      const approved = await runModeration();
      if (approved) {
        navigate('/review');
      } else {
        setStep(2);
      }
    }
  }

  function handleSelectFile(file: File | null) {
    setPhotoError(null);
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setPhotoError('Use uma imagem JPG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setPhotoError('Arquivo muito grande. Limite de 5 MB.');
      return;
    }
    fileToDataUrl(file).then((dataUrl) => setRawImage(dataUrl));
  }

  return (
    <Container size="default" className="py-12 md:py-16">
      <Eyebrow tone="gold" className="mb-3 text-center">
        Composição da sua presença
      </Eyebrow>
      <h1 className="mb-2 text-center font-display text-3xl text-parchment md:text-4xl">
        Três passos para entrar na taça.
      </h1>
      <p className="mx-auto mb-10 max-w-xl text-center text-sm leading-relaxed text-neutral-100 md:text-base">
        Cada passo é um ato cerimonial. Preencha com calma — você pode revisar tudo antes de
        eternizar.
      </p>

      <div className="mx-auto mb-10 max-w-2xl">
        <Stepper currentStep={draft.step} totalSteps={3} labels={STEP_LABELS} />
      </div>

      <Card tone="raised" padding="lg" className="mx-auto max-w-2xl">
        <AnimatePresence mode="wait">
          {draft.step === 1 ? (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={transition}
              className="flex flex-col gap-6"
            >
              <div>
                <Eyebrow tone="gold" className="mb-2">
                  Passo 01 · Nome
                </Eyebrow>
                <h2 className="font-display text-2xl text-parchment md:text-3xl">
                  Como você quer ser eternizada?
                </h2>
              </div>

              <TextField
                label="Seu nome"
                placeholder="Ex.: Maria Silva"
                value={draft.displayName}
                onChange={(e) => setField('displayName', e.target.value)}
                maxLength={MAX_NAME}
                showCounter
                hint="Pode ser nome real, apelido ou nome de batismo na torcida."
                error={nameError ?? undefined}
                autoFocus
              />
            </motion.div>
          ) : null}

          {draft.step === 2 ? (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={transition}
              className="flex flex-col gap-6"
            >
              <div>
                <Eyebrow tone="gold" className="mb-2">
                  Passo 02 · Frase
                </Eyebrow>
                <h2 className="font-display text-2xl text-parchment md:text-3xl">
                  Uma frase que vai durar para sempre.
                </h2>
              </div>

              <TextArea
                label="Sua frase"
                placeholder="Pela minha filha, sonhe alto."
                value={draft.message}
                onChange={(e) => setField('message', e.target.value)}
                maxLength={MAX_MSG}
                rows={3}
                showCounter
                hint="Curta, pessoal, sem urgência. É o que vai ficar gravado."
                error={messageError ?? undefined}
              />

              <div className="flex flex-col gap-3">
                <p className="font-mono text-xs uppercase tracking-stamp text-champagne">
                  Sugestões
                </p>
                <div className="flex flex-wrap gap-2">
                  {MESSAGE_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setField('message', s)}
                      className="rounded-full border border-gold/25 bg-night/40 px-3 py-1.5 text-xs italic leading-snug text-champagne transition-colors duration-fast hover:border-gold/60 hover:text-parchment"
                    >
                      “{s}”
                    </button>
                  ))}
                </div>
              </div>

              {draft.moderation.status === 'rejected' && draft.moderation.reason ? (
                <p
                  role="alert"
                  className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error"
                >
                  {draft.moderation.reason}
                </p>
              ) : null}
            </motion.div>
          ) : null}

          {draft.step === 3 ? (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={transition}
              className="flex flex-col gap-6"
            >
              <div>
                <Eyebrow tone="gold" className="mb-2">
                  Passo 03 · Foto
                </Eyebrow>
                <h2 className="font-display text-2xl text-parchment md:text-3xl">
                  O rosto que vai compor a taça.
                </h2>
              </div>

              {rawImage ? (
                <ImageCropper
                  imageSrc={rawImage}
                  onConfirm={(dataUrl) => {
                    setField('photoDataUrl', dataUrl);
                    setRawImage(null);
                    setPhotoError(null);
                  }}
                  onCancel={() => setRawImage(null)}
                />
              ) : draft.photoDataUrl ? (
                <div className="flex flex-col items-center gap-4">
                  <div className="relative inline-block rounded-md border-2 border-gold/60 p-2 shadow-goldSoft">
                    <img
                      src={draft.photoDataUrl}
                      alt="Sua foto cortada"
                      className="h-56 w-56 rounded-sm object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-mono text-xs uppercase tracking-stamp text-champagne underline-offset-4 hover:underline"
                  >
                    Trocar foto
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-gold/40 bg-night/40 px-6 py-12 text-center transition-colors duration-fast hover:border-gold hover:bg-night/60"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-10 w-10 text-gold"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <path d="M17 8l-5-5-5 5" />
                    <path d="M12 3v12" />
                  </svg>
                  <span className="font-display text-lg text-parchment">
                    Selecione uma foto
                  </span>
                  <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
                    JPG, PNG ou WebP — até 5 MB
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleSelectFile(e.target.files?.[0] ?? null)}
              />

              {photoError ? (
                <p role="alert" className="text-center text-sm text-error">
                  {photoError}
                </p>
              ) : null}

              {draft.moderation.status === 'rejected' && draft.moderation.reason ? (
                <p
                  role="alert"
                  className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error"
                >
                  {draft.moderation.reason}
                </p>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between gap-3 border-t border-gold/15 pt-6">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={prev}
            disabled={draft.step === 1 || draft.moderation.status === 'pending'}
          >
            ← Voltar
          </Button>

          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleNext}
            disabled={draft.moderation.status === 'pending'}
          >
            {draft.step === 3
              ? draft.moderation.status === 'pending'
                ? 'Verificando…'
                : 'Continuar para revisão →'
              : 'Próximo passo →'}
          </Button>
        </div>
      </Card>

      <CeremonialLoader
        open={draft.moderation.status === 'pending'}
        kicker="Verificação cerimonial"
        title="Estamos conferindo o seu registro."
        messages={VALIDATION_MESSAGES}
      />
    </Container>
  );
}
